import { LeadsListV2Component } from "../../features/leads/components/LeadsListV2";
import { useMemo, useState, useEffect } from "react";
import { Input } from "@components/shadcn-ui/input";
import useLeadsV2 from "features/leads/customHooks/useLeadsV2";
import CSVDownloadButton from "features/downloadCsv/DownloadToCsv";
import { MultiSelect } from "@components/ui/MultiSelect";
import { INDIAN_CITIES } from "../../features/loanApplications/formFieldsConfig/indiaStatesList";
import { ContactV2Request } from "../../features/leads/services/leadsServiceV2";
import { Button } from "@components/shadcn-ui/button";
import { RefreshCw, Search, Settings } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@components/shadcn-ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@components/shadcn-ui/accordian";
import { useIsMobile } from "@customHooks/use-mobile";

export const LeadListPageV2 = () => {
  // Simplified filter states
  const [selectedField, setSelectedField] = useState<string>("all");
  const [searchValue, setSearchValue] = useState("");
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [pageSize, setPageSize] = useState(20);

  // Mobile detection
  const isMobile = useIsMobile();

  // V2 API hook
  const {
    leads,
    pagination,
    isLoading,
    error,
    searchLeads,
    loadNextPage,
    loadPreviousPage,
    resetSearch,
    canGoBack,
    canGoNext,
  } = useLeadsV2();

  // Build search request based on simplified filters
  const buildSearchRequest = useMemo((): ContactV2Request => {
    const request: ContactV2Request = {
      page: 1,
      pageSize,
      cities: selectedCities.length > 0 ? selectedCities : undefined,
      sortBy,
      sortOrder,
    };

    // Apply field-specific filter based on dropdown selection
    if (searchValue.trim()) {
      switch (selectedField) {
        case "firstName":
          request.firstName = searchValue.trim();
          break;
        case "lastName":
          request.lastName = searchValue.trim();
          break;
        case "email":
          request.email = searchValue.trim();
          break;
        case "phoneNumber":
          request.phoneNumber = searchValue.trim();
          break;
        case "all":
        default:
          // For "all" field search, we'll use phoneNumber as it's most efficient
          // and handle client-side filtering for other fields
          break;
      }
    }

    return request;
  }, [selectedField, searchValue, selectedCities, sortBy, sortOrder, pageSize]);

  const handleReset = () => {
    setSelectedField("all");
    setSearchValue("");
    setSelectedCities([]);
    setSortBy("createdAt");
    setSortOrder("desc");
    setPageSize(20);
    resetSearch();
    
    // Trigger search with reset values
    const resetRequest: ContactV2Request = {
      page: 1,
      pageSize: 20,
      sortBy: "createdAt",
      sortOrder: "desc",
    };
    searchLeads(resetRequest);
  };

  const handleManualSearch = () => {
    const request = buildSearchRequest;
    searchLeads(request);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleManualSearch();
    }
  };

  // Initial search on component mount
  useEffect(() => {
    const initialRequest: ContactV2Request = {
      page: 1,
      pageSize: 20,
      sortBy: "createdAt",
      sortOrder: "desc",
    };
    searchLeads(initialRequest);
  }, [searchLeads]);

  // Filter leads based on search value for "all" field search (client-side)
  const filteredLeads = useMemo(() => {
    if (!searchValue.trim() || selectedField !== "all") return leads;
    
    return leads.filter(({ firstName, lastName, phoneNumber, email, city }) => {
      const query = searchValue.toLowerCase();
      return (
        firstName?.toLowerCase()?.includes(query) ||
        lastName?.toLowerCase()?.includes(query) ||
        phoneNumber?.includes(searchValue) ||
        email?.toLowerCase()?.includes(query) ||
        city?.toLowerCase()?.includes(query)
      );
    });
  }, [searchValue, selectedField, leads]);

  // Field options for the dropdown
  const fieldOptions = [
    { value: "all", label: "All Fields" },
    { value: "firstName", label: "First Name" },
    { value: "lastName", label: "Last Name" },
    { value: "email", label: "Email" },
    { value: "phoneNumber", label: "Phone Number" },
  ];

  // Get placeholder text based on selected field
  const getPlaceholder = () => {
    switch (selectedField) {
      case "firstName": return "Search by first name...";
      case "lastName": return "Search by last name...";
      case "email": return "Search by email...";
      case "phoneNumber": return "Search by phone number...";
      default: return "Search across all fields...";
    }
  };

  return (
    <div className="h-full flex-1 flex-col md:flex">
      <div className="flex items-center justify-center md:justify-between mb-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Welcome back!</h2>
          <p className="text-muted-foreground">
            Here&apos;s a list of all the Leads! (V2 - Advanced Search)
          </p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
          Error: {error}
        </div>
      )}

      {/* Responsive Search Filter - Accordion for Mobile, Regular for Desktop */}
      {isMobile ? (
        <Accordion type="single" collapsible className="mb-4">
          <AccordionItem value="filters">
            <AccordionTrigger className="px-4 py-3 border rounded-t-lg bg-gray-50 hover:bg-gray-100">
              <div className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                <span>Search & Filter Options</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 border-x border-b rounded-b-lg bg-gray-50">
              <div className="space-y-3">
                {/* Search Row */}
                <div className="flex gap-2">
                  <Select value={selectedField} onValueChange={setSelectedField}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {fieldOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={getPlaceholder()}
                    className="flex-1"
                  />
                </div>

                {/* City Filter */}
                <MultiSelect
                  options={INDIAN_CITIES}
                  selected={selectedCities}
                  onChange={setSelectedCities}
                  placeholder="Filter by cities..."
                  className="w-full"
                />

                {/* Sorting Controls */}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-xs font-medium text-gray-600">Sort by</label>
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="createdAt">Created Date</SelectItem>
                        <SelectItem value="firstName">First Name</SelectItem>
                        <SelectItem value="lastName">Last Name</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="phoneNumber">Phone</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="text-xs font-medium text-gray-600">Order</label>
                    <Select value={sortOrder} onValueChange={setSortOrder}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="desc">Descending</SelectItem>
                        <SelectItem value="asc">Ascending</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="text-xs font-medium text-gray-600">Page size</label>
                    <Select value={pageSize.toString()} onValueChange={(value) => setPageSize(Number(value))}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Action Buttons at Bottom */}
                <div className="flex gap-2 pt-2 border-t border-gray-200">
                  <Button
                    onClick={handleManualSearch}
                    disabled={isLoading}
                    size="sm"
                    className="px-3"
                  >
                    <Search className="h-4 w-4" />
                  </Button>
                  <Button
                    onClick={handleReset}
                    variant="outline"
                    disabled={isLoading}
                    size="sm"
                    className="px-3"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                  <CSVDownloadButton data={filteredLeads} filename="leads-v2.csv" />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      ) : (
        /* Desktop Layout */
        <div className="space-y-3 mb-4 p-4 border rounded-lg bg-gray-50">
          <div className="flex flex-wrap items-center gap-2">
            {/* Field Selection Dropdown */}
            <Select value={selectedField} onValueChange={setSelectedField}>
              <SelectTrigger className="w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {fieldOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Single Search Input */}
            <Input
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={getPlaceholder()}
              className="flex-1 min-w-[300px]"
            />
            
            {/* City Multi-Select */}
            <MultiSelect
              options={INDIAN_CITIES}
              selected={selectedCities}
              onChange={setSelectedCities}
              placeholder="Filter by cities..."
              className="md:w-[300px]"
            />
          </div>

          {/* Sorting and Page Size Controls */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Sort by:</label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="createdAt">Created Date</SelectItem>
                  <SelectItem value="firstName">First Name</SelectItem>
                  <SelectItem value="lastName">Last Name</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="phoneNumber">Phone</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Order:</label>
              <Select value={sortOrder} onValueChange={setSortOrder}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="desc">Descending</SelectItem>
                  <SelectItem value="asc">Ascending</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Page size:</label>
              <Select value={pageSize.toString()} onValueChange={(value) => setPageSize(Number(value))}>
                <SelectTrigger className="w-[80px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Action Buttons at Bottom */}
          <div className="flex gap-2 pt-2 border-t border-gray-200">
            <Button
              onClick={handleManualSearch}
              disabled={isLoading}
              size="sm"
              className="px-3"
            >
              <Search className="h-4 w-4" />
            </Button>

            <Button
              onClick={handleReset}
              variant="outline"
              disabled={isLoading}
              size="sm"
              className="px-3"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>

            <CSVDownloadButton data={filteredLeads} filename="leads-v2.csv" />
          </div>
        </div>
      )}

      {/* Leads List with Pagination */}
      <LeadsListV2Component
        leads={filteredLeads}
        pagination={pagination}
        isLoading={isLoading}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        canGoBack={canGoBack}
        canGoNext={canGoNext}
        searchQuery={searchValue}
      />
    </div>
  );
};
